<template>
  <div class="portfolio-interface">
    <v-card class="portfolio-card">
      <v-card-title class="portfolio-title">
        <v-icon color="primary" class="mr-2">mdi-chart-pie</v-icon>
        Portfolio Multi-Chain
      </v-card-title>
      
      <v-card-text>
        <!-- Resumo do Portfolio -->
        <div class="portfolio-summary mb-6">
          <div class="d-flex flex-wrap">
            <div class="portfolio-summary-chart">
              <div class="text-h6 mb-2">Distribuição por Blockchain</div>
              <div class="chart-placeholder">
                <!-- Aqui seria implementado um gráfico de pizza real -->
                <div class="chart-circle">
                  <div class="chart-segment segment-quarnx" style="transform: rotate(0deg); clip-path: polygon(50% 50%, 50% 0%, 100% 0%, 100% 50%)"></div>
                  <div class="chart-segment segment-eth" style="transform: rotate(90deg); clip-path: polygon(50% 50%, 50% 0%, 75% 0%, 100% 25%, 100% 50%)"></div>
                  <div class="chart-segment segment-bsc" style="transform: rotate(135deg); clip-path: polygon(50% 50%, 50% 0%, 90% 0%, 100% 25%)"></div>
                  <div class="chart-segment segment-dot" style="transform: rotate(180deg); clip-path: polygon(50% 50%, 50% 0%, 65% 0%)"></div>
                  <div class="chart-center"></div>
                </div>
              </div>
            </div>
            
            <div class="portfolio-summary-stats">
              <div class="text-h6 mb-4">Valor Total do Portfolio</div>
              <div class="text-h4 mb-4">$6,938.75 USD</div>
              
              <div class="portfolio-network-stats">
                <div class="network-stat" v-for="(stat, index) in networkStats" :key="index">
                  <v-avatar size="24" :color="stat.color" class="mr-2"></v-avatar>
                  <div class="network-stat-info">
                    <div class="network-name">{{ stat.name }}</div>
                    <div class="network-value">${{ stat.value }} ({{ stat.percentage }}%)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Abas de Grupos de Ativos -->
        <v-tabs v-model="activeTab" slider-color="primary" class="mb-4">
          <v-tab value="all">Todos os Ativos</v-tab>
          <v-tab value="quarnx">QuarnX</v-tab>
          <v-tab value="eth">Ethereum</v-tab>
          <v-tab value="bsc">BSC</v-tab>
          <v-tab value="other">Outras Chains</v-tab>
        </v-tabs>
        
        <v-window v-model="activeTab">
          <v-window-item v-for="tab in ['all', 'quarnx', 'eth', 'bsc', 'other']" :key="tab" :value="tab">
            <v-data-table
              :headers="tokenTableHeaders"
              :items="getFilteredTokens(tab)"
              :items-per-page="5"
              class="elevation-1 token-table"
            >
              <template v-slot:item.token="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="32" class="mr-2">
                    <v-img :src="item.logo" alt="Token Logo"></v-img>
                  </v-avatar>
                  <div>
                    <div>{{ item.symbol }}</div>
                    <div class="text-caption">{{ item.name }}</div>
                  </div>
                </div>
              </template>
              
              <template v-slot:item.network="{ item }">
                <v-chip
                  :color="getNetworkColor(item.network)"
                  variant="outlined"
                  size="small"
                  label
                >
                  {{ item.network }}
                </v-chip>
              </template>
              
              <template v-slot:item.balance="{ item }">
                {{ formatTokenAmount(item.balance, item.decimals) }}
              </template>
              
              <template v-slot:item.valueUsd="{ item }">
                ${{ item.valueUsd.toFixed(2) }}
              </template>
              
              <template v-slot:item.actions="{ item }">
                <div class="d-flex">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    class="mr-1"
                    @click="showSendDialog(item)"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                  
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    class="mr-1"
                    @click="showSwapDialog(item)"
                  >
                    <v-icon>mdi-swap-horizontal</v-icon>
                  </v-btn>
                  
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    @click="showBridgeDialog(item)"
                  >
                    <v-icon>mdi-bridge</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-window-item>
        </v-window>
        
        <!-- Histórico de Transações Cross-Chain -->
        <div class="transaction-history mt-8">
          <h3 class="text-h6 mb-4">Histórico de Transações Cross-Chain</h3>
          
          <v-data-table
            :headers="transactionTableHeaders"
            :items="recentTransactions"
            :items-per-page="5"
            class="elevation-1"
          >
            <template v-slot:item.type="{ item }">
              <v-chip
                :color="getTransactionTypeColor(item.type)"
                size="small"
              >
                {{ item.type }}
              </v-chip>
            </template>
            
            <template v-slot:item.fromNetwork="{ item }">
              <v-chip
                :color="getNetworkColor(item.fromNetwork)"
                variant="outlined"
                size="small"
                label
              >
                {{ item.fromNetwork }}
              </v-chip>
            </template>
            
            <template v-slot:item.toNetwork="{ item }">
              <v-chip
                :color="getNetworkColor(item.toNetwork)"
                variant="outlined"
                size="small"
                label
              >
                {{ item.toNetwork }}
              </v-chip>
            </template>
            
            <template v-slot:item.date="{ item }">
              {{ formatDate(item.date) }}
            </template>
            
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                size="small"
              >
                {{ item.status }}
              </v-chip>
            </template>
            
            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                variant="text"
                size="small"
                color="primary"
                @click="viewTransaction(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Diálogo para enviar token -->
    <v-dialog v-model="sendDialog.show" max-width="500">
      <v-card>
        <v-card-title>Enviar {{ sendDialog.token?.symbol }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="sendDialog.recipient"
            label="Endereço do Destinatário"
            placeholder="0x..."
            variant="outlined"
          ></v-text-field>
          
          <v-text-field
            v-model="sendDialog.amount"
            :label="t('dex.amount')"
            type="number"
            variant="outlined"
            :suffix="sendDialog.token?.symbol"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey-darken-1" variant="text" @click="sendDialog.show = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="sendToken">
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'

export default {
  name: 'PortfolioInterface',
  
  data() {
    return {
      activeTab: 'all',
      tokenTableHeaders: [
        { title: 'Token', key: 'token', sortable: false },
        { title: 'Rede', key: 'network' },
        { title: 'Saldo', key: 'balance' },
        { title: 'Valor USD', key: 'valueUsd' },
        { title: 'Ações', key: 'actions', sortable: false }
      ],
      transactionTableHeaders: [
        { title: 'Tipo', key: 'type' },
        { title: 'De', key: 'fromNetwork' },
        { title: 'Para', key: 'toNetwork' },
        { title: 'Token', key: 'token' },
        { title: 'Quantidade', key: 'amount' },
        { title: 'Data', key: 'date' },
        { title: 'Status', key: 'status' },
        { title: 'Ações', key: 'actions', sortable: false }
      ],
      networkStats: [
        { name: 'QuarnX', value: '3,126.25', percentage: 45, color: 'primary' },
        { name: 'Ethereum', value: '2,887.50', percentage: 42, color: '#627EEA' },
        { name: 'BSC', value: '500.00', percentage: 7, color: '#F3BA2F' },
        { name: 'Polkadot', value: '425.00', percentage: 6, color: '#E6007A' }
      ],
      // Tokens simulados do usuário
      userTokens: [
        {
          symbol: window.__QUARNX_COIN__ || 'tKinX',
          name: `${window.__QUARNX_COIN__ || 'tKinX'} Token`,
          network: 'QuarnX',
          balance: '1000000000000000000000',
          decimals: 18,
          valueUsd: 3126.25,
          logo: '/assets/tokens/kinx.png'
        },
        {
          symbol: 'QUSD',
          name: 'QuarnX USD',
          network: 'QuarnX',
          balance: '5000000000', // 5000 QUSD
          decimals: 6,
          valueUsd: 5000.00,
          logo: '/assets/tokens/qusd.png'
        },
        {
          symbol: 'ETH',
          name: 'Ethereum',
          network: 'Ethereum',
          balance: '5000000000000000000', // 5 ETH
          decimals: 18,
          valueUsd: 2887.50,
          logo: '/assets/tokens/eth.png'
        },
        {
          symbol: 'USDT',
          name: 'Tether USD',
          network: 'Ethereum',
          balance: '10000000000', // 10000 USDT
          decimals: 6,
          valueUsd: 10000.00,
          logo: '/assets/tokens/usdt.png'
        },
        {
          symbol: 'BNB',
          name: 'Binance Coin',
          network: 'BSC',
          balance: '50000000000000000000', // 50 BNB
          decimals: 18,
          valueUsd: 500.00,
          logo: '/assets/tokens/bnb.png'
        },
        {
          symbol: 'DOT',
          name: 'Polkadot',
          network: 'Polkadot',
          balance: '1000000000000', // 100 DOT
          decimals: 10,
          valueUsd: 425.00,
          logo: '/assets/tokens/dot.png'
        },
      ],
      // Transações simuladas
      recentTransactions: [
        {
          id: 'tx1',
          type: 'Bridge',
          fromNetwork: 'QuarnX',
          toNetwork: 'Ethereum',
          token: window.__QUARNX_COIN__ || 'tKinX',
          amount: '50',
          date: new Date('2025-04-21T18:30:00'),
          status: 'Completado',
          hash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef'
        },
        {
          id: 'tx2',
          type: 'Swap',
          fromNetwork: 'QuarnX',
          toNetwork: 'QuarnX',
          token: `${window.__QUARNX_COIN__ || 'tKinX'} → QUSD`,
          amount: '100',
          date: new Date('2025-04-21T17:45:00'),
          status: 'Completado',
          hash: '0xabcdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890'
        },
        {
          id: 'tx3',
          type: 'Bridge',
          fromNetwork: 'Ethereum',
          toNetwork: 'BSC',
          token: 'USDT',
          amount: '250',
          date: new Date('2025-04-21T15:20:00'),
          status: 'Pendente',
          hash: '0x7890abcdef1234567890abcdef1234567890abcdef1234567890abcdef123456'
        },
        {
          id: 'tx4',
          type: 'Envio',
          fromNetwork: 'QuarnX',
          toNetwork: 'QuarnX',
          token: window.__QUARNX_COIN__ || 'tKinX',
          amount: '25',
          date: new Date('2025-04-21T12:10:00'),
          status: 'Completado',
          hash: '0xdef1234567890abcdef1234567890abcdef1234567890abcdef1234567890abc'
        },
        {
          id: 'tx5',
          type: 'Recebimento',
          fromNetwork: 'Ethereum',
          toNetwork: 'Ethereum',
          token: 'ETH',
          amount: '0.5',
          date: new Date('2025-04-20T22:05:00'),
          status: 'Completado',
          hash: '0x567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef1234'
        }
      ],
      // Diálogos
      sendDialog: {
        show: false,
        token: null,
        recipient: '',
        amount: ''
      }
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    }
  },
  
  methods: {
    getFilteredTokens(tab) {
      if (tab === 'all') {
        return this.userTokens
      } else if (tab === 'quarnx') {
        return this.userTokens.filter(token => token.network === 'QuarnX')
      } else if (tab === 'eth') {
        return this.userTokens.filter(token => token.network === 'Ethereum')
      } else if (tab === 'bsc') {
        return this.userTokens.filter(token => token.network === 'BSC')
      } else if (tab === 'other') {
        return this.userTokens.filter(token => !['QuarnX', 'Ethereum', 'BSC'].includes(token.network))
      }
      return []
    },
    
    formatTokenAmount(amount, decimals) {
      if (!amount) return '0'
      return (parseFloat(amount) / Math.pow(10, decimals)).toString()
    },
    
    getNetworkColor(network) {
      const colors = {
        'QuarnX': 'primary',
        'Ethereum': '#627EEA',
        'BSC': '#F3BA2F',
        'Polkadot': '#E6007A'
      }
      return colors[network] || 'grey'
    },
    
    getTransactionTypeColor(type) {
      const colors = {
        'Bridge': 'purple',
        'Swap': 'blue',
        'Envio': 'orange',
        'Recebimento': 'green'
      }
      return colors[type] || 'grey'
    },
    
    getStatusColor(status) {
      const colors = {
        'Completado': 'success',
        'Pendente': 'warning',
        'Falha': 'error'
      }
      return colors[status] || 'grey'
    },
    
    formatDate(date) {
      if (!(date instanceof Date)) return ''
      
      const today = new Date()
      const isToday = date.getDate() === today.getDate() && 
                       date.getMonth() === today.getMonth() && 
                       date.getFullYear() === today.getFullYear()
      
      if (isToday) {
        return `Hoje ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
      }
      
      return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },
    
    showSendDialog(token) {
      this.sendDialog.token = token
      this.sendDialog.recipient = ''
      this.sendDialog.amount = ''
      this.sendDialog.show = true
    },
    
    showSwapDialog(token) {
      // Simulação apenas
      alert(`Interface de swap para ${token.symbol} será implementada em breve.`)
    },
    
    showBridgeDialog(token) {
      // Simulação apenas
      alert(`Interface de bridge para ${token.symbol} será implementada em breve.`)
    },
    
    sendToken() {
      // Simulação de envio
      alert(`Esta é uma versão demo. O envio de ${this.sendDialog.amount} ${this.sendDialog.token.symbol} para ${this.sendDialog.recipient} seria processado aqui.`)
      this.sendDialog.show = false
    },
    
    viewTransaction(transaction) {
      // Simulação de visualização de transação
      alert(`Visualização da transação ${transaction.id} será implementada em breve.`)
    }
  }
}
</script>

<style scoped>
.portfolio-card {
  border-radius: 16px;
}

.portfolio-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.portfolio-summary {
  padding: 16px;
  background-color: rgba(var(--v-theme-surface-variant), 0.08);
  border-radius: 12px;
}

.portfolio-summary-chart {
  flex: 1;
  min-width: 250px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.portfolio-summary-stats {
  flex: 1;
  min-width: 250px;
  padding: 16px;
}

.chart-placeholder {
  width: 200px;
  height: 200px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-circle {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
}

.chart-segment {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.segment-quarnx {
  background-color: var(--v-theme-primary);
}

.segment-eth {
  background-color: #627EEA;
}

.segment-bsc {
  background-color: #F3BA2F;
}

.segment-dot {
  background-color: #E6007A;
}

.chart-center {
  position: absolute;
  width: 80px;
  height: 80px;
  background-color: var(--v-theme-surface);
  border-radius: 50%;
}

.network-stat {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.network-stat-info {
  flex: 1;
}

.network-name {
  font-weight: 500;
}

.network-value {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.transaction-history {
  margin-top: 24px;
}
</style>
